import React from 'react';
import { Box } from '@components/Box';
import { Flex } from '@components/Flex';
import { addRendition } from '@utils/photoRendition';
import { EXT_APP_ROOT_URL } from '@utils/urlHelpers.util';
import { findACouplePageData } from '@data-ts/pages/find-an-event';
import { EventType } from '@sections/find/HeroSection/HeroSection.types';
import { getEvenType } from '@sections/find/HeroSection/HeroSection.utils';
import { PlaceholderAvatar } from '@sections/find/HeroSection/components/PlaceholderAvatars/PlaceholderAvatar';

import {
  AvatarImageContainer,
  DateField,
  DetailField,
  DetailFieldMobile,
  ListItemWrapper,
  NameField,
  StyledCoupleAvatar,
  LinksWrapper,
  EventTypeField,
  StyledCTALink,
  DateFieldMobile,
} from './ResultListItem.styles';
import { Integrations } from '@static/js/types';

const analytics = require('/static/js/joy');

const heroSectionData = findACouplePageData.heroSection;

type ListItemProps = {
  ownerFullName: string;
  fianceeFullName?: string;
  eventDate: string;
  website: string;
  photoUrl: string | null;
  eventType: EventType;
  index: number;
  registryPageDisabled: boolean;
  showBorderBottoom: boolean;
};

interface ItemDetailsProps {
  id: string;
  ownerFullName: ListItemProps['ownerFullName'];
  eventDate: ListItemProps['eventDate'];
  website: ListItemProps['website'];
  registryPageDisabled: ListItemProps['registryPageDisabled'];
  websiteClicked: (e: React.MouseEvent) => void;
}

const ItemDetails: React.FC<ItemDetailsProps> = ({ id, ownerFullName, eventDate, website, registryPageDisabled, websiteClicked }) => {
  const registryClicked = (e: React.MouseEvent) => {
    e.preventDefault();
    analytics.segment.sendTelemetryEvent('newmarketing.findACoupleRegistryClicked', { website }, undefined, {
      integrations: { [Integrations.Amplitude]: true },
    });
    window.open(EXT_APP_ROOT_URL + '/' + website + '/registry', '_blank');
  };
  return (
    <>
      <DateFieldMobile tagName="span" variant="dek15" color="mono1">
        {eventDate}
      </DateFieldMobile>
      <LinksWrapper>
        {website && (
          <>
            <StyledCTALink
              href={EXT_APP_ROOT_URL + '/' + website}
              id={`${ownerFullName}-website-${id}`}
              target="_blank"
              typography={'featureHed17'}
              hasChevron={true}
              onClick={websiteClicked}
            >
              {heroSectionData.website}
            </StyledCTALink>
            {!registryPageDisabled ? (
              <Box marginLeft={[5, 0]}>
                <StyledCTALink
                  href={EXT_APP_ROOT_URL + '/' + website + '/registry'}
                  id={`${ownerFullName}-registry-${id}`}
                  onClick={registryClicked}
                  target="_blank"
                  typography={'featureHed17'}
                  hasChevron={true}
                >
                  {heroSectionData.registry}
                </StyledCTALink>
              </Box>
            ) : null}
          </>
        )}
      </LinksWrapper>
    </>
  );
};

export const ResultListItem: React.FC<ListItemProps> = props => {
  const { ownerFullName, fianceeFullName, eventDate, website, photoUrl, eventType, index, registryPageDisabled, showBorderBottoom } = props;

  const websiteClicked = (e: React.MouseEvent) => {
    e.preventDefault();
    analytics.segment.sendTelemetryEvent('newmarketing.findACoupleWebsiteClicked', { website }, undefined, {
      integrations: { [Integrations.Amplitude]: true },
    });
    window.open(EXT_APP_ROOT_URL + '/' + website, '_blank');
  };

  return (
    <ListItemWrapper paddingY={6} paddingX={[5, 0]} showBorderBottoom={showBorderBottoom}>
      <Flex alignItems="center" css={{ gap: 24 }} paddingY={4}>
        {photoUrl ? (
          <a target="blank" id={`${ownerFullName}-website`} onClick={websiteClicked} href={EXT_APP_ROOT_URL + '/' + website}>
            <AvatarImageContainer>
              <StyledCoupleAvatar src={addRendition({ url: photoUrl, renditionSize: 'avatar' })} alt={'couple avatar'} />
            </AvatarImageContainer>
          </a>
        ) : (
          <a target="blank" id={`${ownerFullName}-website`} href={EXT_APP_ROOT_URL + '/' + website}>
            <AvatarImageContainer>
              <PlaceholderAvatar avatarIndex={index % 6} />
            </AvatarImageContainer>
          </a>
        )}

        <Flex onClick={websiteClicked} alignItems="flex-start" css={{ gap: 4 }} flexDirection="column">
          <EventTypeField variant="austinNewsDek24" tagName="span" color="mono1">
            {getEvenType(eventType)}
          </EventTypeField>
          <NameField tagName="span" variant="austinNewsDek24" color="mono1">
            {`${ownerFullName}`}
            {fianceeFullName ? (
              <span>
                {' '}
                & <span className="fiancee">{`${fianceeFullName}`}</span>
              </span>
            ) : null}
          </NameField>
          <DetailFieldMobile>
            <ItemDetails
              id="mobile"
              ownerFullName={ownerFullName}
              eventDate={eventDate}
              website={website}
              registryPageDisabled={registryPageDisabled}
              websiteClicked={websiteClicked}
            />
          </DetailFieldMobile>
        </Flex>
      </Flex>
      <DateField tagName="span" variant="dek15" color="mono1">
        {eventDate}
      </DateField>
      <DetailField>
        <ItemDetails
          id="desktop"
          ownerFullName={ownerFullName}
          eventDate={eventDate}
          website={website}
          registryPageDisabled={registryPageDisabled}
          websiteClicked={websiteClicked}
        />
      </DetailField>
    </ListItemWrapper>
  );
};
